import { tagName } from "@ember-decorators/component"
import Component from "@ember/component"
import { action } from "@ember/object"
import { inject as service } from "@ember/service"
import * as Sentry from "@sentry/browser"
import { cloneCardStackAttributesWithNewIds } from "@shared/lib/card-stack/clone"
import type { CardStack } from "@shared/models/card-stack"
import type { Course } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { objectId } from "@shared/util/object-id"
import hasManyCollectively from "cc-frontend/helpers/has-many-collectively"
import createClassEditTemplateAction from "cc-frontend/lib/actions/app/CREATE_CLASS_EDIT_TEMPLATE"
import createClassEvaluateTemplateAction from "cc-frontend/lib/actions/app/CREATE_CLASS_EVALUATE_TEMPLATE"
import createClassGoToFinalScreenAction from "cc-frontend/lib/actions/app/CREATE_CLASS_GO_TO_FINAL_SCREEN"
import createClassGoToNameAction from "cc-frontend/lib/actions/app/CREATE_CLASS_GO_TO_NAME"
import createClassGoToOrderAction from "cc-frontend/lib/actions/app/CREATE_CLASS_GO_TO_ORDER"
import createClassGoToTemplateChooserAction from "cc-frontend/lib/actions/app/CREATE_CLASS_GO_TO_TEMPLATE_CHOOSER"
import {
  insertNewTemplate,
  prepareSaveAsLessonTemplate,
} from "cc-frontend/lib/actions/course/calendar/COURSE_DATE_SAVE_AS_TEMPLATE"
import { prepareCourseAssignLessonTemplateToWeek } from "cc-frontend/lib/actions/course/COURSE_ASSIGN_LESSON_TEMPLATE_TO_WEEK"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type EmberStore from "cc-frontend/services/ember-store"
import { easeIn, easeOut } from "ember-animated/easings/cosine"
import move from "ember-animated/motions/move"
import { fadeIn, fadeOut } from "ember-animated/motions/opacity"
import { includes, map } from "lodash-es"
import styles from "./create-main.module.css"

const forwardTransition = function* ({ insertedSprites, keptSprites, removedSprites }) {
  insertedSprites.forEach((sprite) => {
    sprite.startAtPixel({ x: window.innerWidth })
    fadeIn(sprite, { easing: easeOut, duration: 500 })
    // move(sprite, { easing: easeOut, duration: 500 })
  })

  keptSprites.forEach(move)

  removedSprites.forEach((sprite) => {
    sprite.endAtPixel({ x: -window.innerWidth })
    // move(sprite, { easing: easeIn, duration: 500 })
    fadeOut(sprite, { easing: easeIn, duration: 500 })
  })
}
const backwardsTransition = function* ({ insertedSprites, keptSprites, removedSprites }) {
  insertedSprites.forEach((sprite) => {
    sprite.startAtPixel({ x: -window.innerWidth })
    fadeIn(sprite, { easing: easeOut, duration: 500 })
    // move(sprite, { easing: easeOut, duration: 500 })
  })

  keptSprites.forEach(move)

  removedSprites.forEach((sprite) => {
    sprite.endAtPixel({ x: window.innerWidth })
    // move(sprite, { easing: easeIn, duration: 500 })
    fadeOut(sprite, { easing: easeIn, duration: 500 })
  })
}

@tagName("")
export default class CreateClassMain extends Component {
  readonly styles = styles

  // args
  course!: Course
  planbook!: Planbook
  rotationCalendar!: RotationCalendar
  close!: Function

  @service declare dispatcher: Dispatcher

  @service("ember-store") declare store: EmberStore

  screen: string = "NAME_AND_MORE"
  // screen: string = "TEMPLATE_LIBRARY"
  selectedTemplate: CardStack | null = null
  templateIdToEdit: string | null = null
  setSmallWidth!: Function
  setNormalWidth!: Function

  animationRules({ oldItems, newItems }) {
    let newVal = newItems[0]
    let oldVal = oldItems[0]
    if (oldVal < newVal) {
      // return toLeft
      // return toUp
      return forwardTransition
    } else {
      // return toRight
      // return toDown
      return backwardsTransition
    }
  }

  @hasManyCollectively("planbook", "course", "courses") courses

  @action
  goToOrder() {
    this.dispatcher.dispatchPreparedAction(createClassGoToOrderAction({}))
    this.setNormalWidth()
    if (this.planbook.relationships.courses.data.length < 2) {
      this.set("screen", "TEMPLATE_LIBRARY")
    } else {
      this.set("screen", "ORDER")
    }
  }

  @action
  goToName() {
    this.dispatcher.dispatchPreparedAction(createClassGoToNameAction({}))
    this.setNormalWidth()
    this.set("screen", "NAME_AND_MORE")
  }

  @action
  maybeGoToTemplateChooser() {
    if (this.course.attributes.isNonInstructional) {
      // Skip the template chooser steps.
      this.goToFinalScreen()
    } else {
      this.goToTemplateChooser()
    }
  }

  @action
  goToTemplateChooser() {
    this.dispatcher.dispatchPreparedAction(createClassGoToTemplateChooserAction({}))
    this.setNormalWidth()
    this.set("screen", "TEMPLATE_LIBRARY")
  }

  @action
  editTemplate() {
    // copy the template
    // go to the edit template screen

    if (this.selectedTemplate === null) throw Error("No selected template to edit")

    let cardStackTemplateIds = map(
      this.planbook.relationships.cardStackTemplates.data,
      (data) => data.id
    )
    let cardStack: CardStack = this.selectedTemplate
    let id = cardStack.id
    let isAlreadyInPlanbook = includes(cardStackTemplateIds, cardStack.id)
    let newCardStackAttributes
    if (isAlreadyInPlanbook !== true) {
      newCardStackAttributes = cloneCardStackAttributesWithNewIds(
        cardStack.attributes,
        cardStack.attributes.parentCardStack,
        !!this.planbook.attributes.flags.unifiedLessons,
        {
          overrideTitle: cardStack.attributes.title
            ? "My " + cardStack.attributes.title
            : "My New Template",
          onMigrationError: (error) => Sentry.captureException(error),
        }
      )
      id = objectId()
      insertNewTemplate(this.store, id, this.planbook.id, newCardStackAttributes)
      this.dispatcher.dispatchPreparedAction(
        prepareSaveAsLessonTemplate({
          newId: id,
          planbookId: this.planbook.id,
          cardStackAttributes: newCardStackAttributes,
        })
      )
    }
    this.set("templateIdToEdit", id)
    this._assignTemplateToWeek(id)
    this.setSmallWidth()
    this.set("screen", "EDIT_TEMPLATE")
    this.dispatcher.dispatchPreparedAction(
      createClassEditTemplateAction({
        cardStackId: id,
        templateTitle: newCardStackAttributes.title,
      })
    )
  }

  @action
  chooseTemplate(template) {
    // set the template for all the course dates in the course
    // redirect to decider screen
    this.dispatcher.dispatchPreparedAction(createClassEvaluateTemplateAction({}))
    this._assignTemplateToWeek(template.id)
    this.set("selectedTemplate", template)
    this.set("screen", "EVALUATE_TEMPLATES")
  }

  @action
  createTemplate(cardStackAttributes) {
    let id = objectId()
    insertNewTemplate(this.store, id, this.planbook.id, cardStackAttributes)
    this.dispatcher.dispatchPreparedAction(
      prepareSaveAsLessonTemplate({
        newId: id,
        planbookId: this.planbook.id,
        cardStackAttributes: cardStackAttributes,
      })
    )
    this._assignTemplateToWeek(id)
    this.set("templateIdToEdit", id)
    this.setSmallWidth()
    // create a blank template
    // set the template for all the courseDates in the course
    // redirect to customize screen

    return this.set("screen", "EDIT_TEMPLATE")
  }

  @action
  goToFinalScreen() {
    this.dispatcher.dispatchPreparedAction(createClassGoToFinalScreenAction({}))
    this.setSmallWidth()
    this.set("screen", "FINAL_SCREEN")
  }

  _assignTemplateToWeek(cardStackId) {
    this.dispatcher.dispatchPreparedAction(
      prepareCourseAssignLessonTemplateToWeek({
        course: this.course,
        cardStackId: cardStackId,
        planbookId: this.planbook.id,
        rotationCalendar: this.rotationCalendar,
      })
    )
  }
}
